import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { arrayBufferToBase64 } from '../lib/crypto';
import { decryptConversationText, ensureConversationKey, encryptConversationText, setConversationKeyRaw } from '../lib/conversation-crypto';
import { t } from '../lib/i18n';
import { connectDmRealtime } from '../lib/realtime/dm';
import { api } from '../lib/trpc/client';

type DmMessage = { messageId: string; senderId: string; senderUsername?: string | null; timestamp: string; content: string };
type Conversation = { dmId: string; partnerUserId: string; partnerUsername: string; updatedAt: string };

export function DmChatPage() {
  const { circleId, dmId } = useParams<{ circleId: string; dmId: string }>();
  const [messages, setMessages] = useState<DmMessage[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [username, setUsername] = useState('');
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [activeCircleName, setActiveCircleName] = useState('');
  const [myUserId, setMyUserId] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  const loadConversations = useCallback(async () => {
    const result = await api.dm.getConversations();
    setConversations(result.conversations);
  }, []);

  const loadMessages = useCallback(async (silent = false) => {
    if (!dmId) {
      return;
    }
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [result, memberResult] = await Promise.all([
        api.dm.getMessages({ dmId, limit: 100 }),
        api.feed.getCircleMembers()
      ]);
      const memberMap = new Map(memberResult.members.map(m => [m.userId, m.scratchUsername]));
      const key = await ensureConversationKey('dm', dmId, result.wrappedKey);

      const decrypted = await Promise.all(
        result.messages.map(async (message): Promise<DmMessage> => {
          try {
            const content = await decryptConversationText(message.ciphertext, key);
            return {
              messageId: message.messageId,
              senderId: message.senderId,
              senderUsername: memberMap.get(message.senderId) ?? null,
              timestamp: message.timestamp,
              content
            };
          } catch {
            return {
              messageId: message.messageId,
              senderId: message.senderId,
              senderUsername: memberMap.get(message.senderId) ?? null,
              timestamp: message.timestamp,
              content: t('[復号失敗]', '[decrypt failed]')
            };
          }
        })
      );

      // Sort chronological (oldest first) for Discord-style bottom flow
      setMessages(decrypted.sort((a, b) => a.timestamp.localeCompare(b.timestamp)));
    } catch (err) {
      if (!silent) {
        setError(err instanceof Error ? err.message : t('メッセージの取得に失敗しました', 'Failed to load messages'));
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [dmId]);

  useEffect(() => {
    setMyUserId(localStorage.getItem('userId'));
    void loadConversations();
    void api.circle.getActiveCircle().then((circle) => setActiveCircleName(circle?.name ?? '')).catch(() => setActiveCircleName(''));
  }, [loadConversations]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  useEffect(() => connectDmRealtime((event) => {
    void loadConversations();
    if (!dmId || !event.dmId || event.dmId === dmId || event.type === 'dm_conversation') {
      void loadMessages(true);
    }
  }), [dmId, loadConversations, loadMessages]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleCreateConversation = async () => {
    const partnerUsername = username.trim();
    if (!partnerUsername) {
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
      const raw = (await crypto.subtle.exportKey('raw', key)) as ArrayBuffer;
      const rawBase64 = arrayBufferToBase64(raw);
      const created = await api.dm.createConversation({
        partnerUsername,
        fingerprintVerified: false,
        wrappedKeyForSelf: rawBase64,
        wrappedKeyForPartner: rawBase64
      });
      setConversationKeyRaw('dm', created.dmId, rawBase64);
      setUsername('');
      await loadConversations();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('会話の作成に失敗しました', 'Failed to create conversation'));
    } finally {
      setCreating(false);
    }
  };

  const sendMessage = async () => {
    const text = draft.trim();
    if (!dmId || !text) {
      return;
    }
    setSending(true);
    setError(null);
    try {
      const keyInfo = await api.dm.getMessages({ dmId, limit: 1 });
      const key = await ensureConversationKey('dm', dmId, keyInfo.wrappedKey);
      const timestamp = new Date().toISOString();
      const ciphertext = await encryptConversationText(text, key);
      await api.dm.sendMessage({ dmId, ciphertext, timestamp });
      
      setDraft('');
      await Promise.all([loadMessages(true), loadConversations()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('メッセージ送信に失敗しました', 'Failed to send message'));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  };

  const currentPartner = conversations.find(c => c.dmId === dmId);

  return (
    <main className="h-[calc(100vh-4rem)] overflow-hidden rounded-xl border border-border bg-card/60">
      <header className="flex items-center justify-between px-4 py-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('DMチャット', 'DM Chat')}</h1>
          <p className="text-xs text-muted-foreground">{t('サークル', 'Circle')}: {activeCircleName || t('未選択', 'Not selected')}</p>
        </div>
        <Button variant="outline" asChild>
          <Link to={circleId ? `/${circleId}/dm` : '/circles'}>{t('一覧へ', 'Back to list')}</Link>
        </Button>
      </header>

      <div className="grid h-[calc(100%-4.5rem)] grid-cols-1 md:grid-cols-[320px_1fr]">
        <aside className="border-r border-border p-3">
          <Card className="mb-3">
            <div className="p-3 space-y-2">
              <p className="text-sm font-medium">{t('新しいDM', 'New DM')}</p>
              <div className="flex gap-1">
                <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder={t('相手のユーザー名', 'Partner')} />
                <Button size="sm" onClick={handleCreateConversation} disabled={creating}>{creating ? '...' : '+'}</Button>
              </div>
            </div>
          </Card>
          <div className="space-y-2">
            {conversations.map((conversation) => (
              <Link
                key={conversation.dmId}
                to={circleId ? `/${circleId}/dm/${conversation.dmId}` : '/circles'}
                className={`block rounded-md border px-3 py-2 text-sm ${conversation.dmId === dmId ? 'border-primary bg-muted' : 'border-border hover:bg-muted'}`}
              >
                <p className="font-medium">{conversation.partnerUsername}</p>
                <p className="text-xs text-muted-foreground">{new Date(conversation.updatedAt).toLocaleString()}</p>
              </Link>
            ))}
          </div>
        </aside>

        <section className="flex h-full flex-col bg-background">
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4">
            <div className="flex min-h-full flex-col justify-end py-4">
              {/* Welcome Banner */}
              {!loading && messages.length < 50 && (
                <div className="mb-8 px-4 border-b border-border pb-8">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted text-2xl font-bold text-muted-foreground border border-border">
                    {currentPartner?.partnerUsername.slice(0, 2).toUpperCase() || '?'}
                  </div>
                  <h2 className="text-2xl font-bold">{currentPartner?.partnerUsername}</h2>
                  <p className="text-muted-foreground text-sm">
                    {t('これは', 'This is the beginning of your direct message history with')} <strong>@{currentPartner?.partnerUsername}</strong>.
                  </p>
                </div>
              )}

              {loading ? (
                <div className="space-y-4 p-4">
                  <Skeleton className="h-12 w-3/4" />
                  <Skeleton className="h-12 w-1/2" />
                </div>
              ) : (
                <div className="space-y-1">
                {messages.map((message, idx) => {
                  const isSameAuthor = idx > 0 && messages[idx - 1]?.senderId === message.senderId;
                  const time = new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  const date = new Date(message.timestamp).toLocaleDateString();
                  
                  return (
                    <div key={message.messageId} className={`group flex items-start gap-4 px-4 hover:bg-muted/50 ${!isSameAuthor ? 'mt-4 py-1' : 'mt-0.5 py-0'}`}>
                      {!isSameAuthor ? (
                        <div className="mt-1 h-10 w-10 shrink-0 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-primary-foreground">
                          {(message.senderUsername || 'U').slice(0, 2).toUpperCase()}
                        </div>
                      ) : (
                        <div className="w-10 shrink-0 text-[9px] text-center text-muted-foreground opacity-0 group-hover:opacity-100 pt-1 leading-none">
                          {time}
                        </div>
                      )}
                      <div className="flex-1 overflow-hidden">
                        {!isSameAuthor && (
                          <div className="flex items-baseline gap-2">
                            <span className="font-bold hover:underline cursor-pointer text-sm">
                              {message.senderId === myUserId ? t('自分', 'Me') : (message.senderUsername || message.senderId)}
                            </span>
                            <span className="text-[10px] text-muted-foreground">{date} {time}</span>
                          </div>
                        )}
                        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                          {message.content}
                        </p>
                      </div>
                    </div>
                  );
                })}
                </div>
              )}
              {error && (
                <div className="px-4 pt-4">
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-border p-4">
            <div className="relative flex flex-col rounded-lg bg-muted/50 border border-border px-4 py-2.5">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                className="min-h-[44px] resize-none border-none bg-transparent p-0 text-sm focus-visible:ring-0 placeholder:text-muted-foreground"
                placeholder={t(`@${currentPartner?.partnerUsername || ''} へのメッセージ`, `Message @${currentPartner?.partnerUsername || ''}`)}
              />
              <div className="flex items-center justify-between pt-1">
                 <span className="text-[10px] text-muted-foreground">
                   {t('Enterで送信 / Shift+Enterで改行', 'Enter to send / Shift+Enter for new line')}
                 </span>
                 <Button 
                   size="sm" 
                   onClick={sendMessage} 
                   disabled={sending || !draft.trim()}
                   className="h-7 text-xs"
                 >
                   {sending ? '...' : t('送信', 'Send')}
                 </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
